import {useEffect, useState} from 'react';
import {API_URL} from '../settings/local_settings';
import {APIClient} from '../utils/APIClient';
import {LocalStorage} from '../utils/LocalStorage';

export const REPORT_MODALITY_DATA_EXPLORER = 'DATA_EXPLORER';
export const REPORT_MODALITY_CLIENT_REPORTS = 'REPORT_WITH_TEMPLATES';

/**
 * La modalidad de reportes de la agencia del usuario: vistas del Explorador (V1) o Informes
 * sobre bloques. Es el interruptor de coexistencia del frente Informes — por agencia, sin
 * deploy, reversible — y viaja en `get-logged-user-data/` como `agency_report_modality`.
 *
 * Seed sincrónico desde LocalStorage (que `ClientTabs` ya mantiene) para no parpadear, y
 * refresh con `{cache: true}`: comparte el request con los otros callers del mismo endpoint.
 * No va por `UserModulesContext` a propósito: su fetch sólo corre en el marco 2.1, y esta
 * pantalla se sirve también en el clásico.
 */
export function useReportModality() {
    const [modality, setModality] = useState(() => LocalStorage.fetchAgencyReportModality());
    const [seeded] = useState(() => LocalStorage.hasAgencyReportModality());
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        new APIClient(API_URL + 'get-logged-user-data/').call('GET', {}, {cache: true}).then((data) => {
            if (cancelled) return;
            if (!data || data.status !== 'OK') { setLoaded(true); return; }
            const fresh = data.agency_report_modality || REPORT_MODALITY_DATA_EXPLORER;
            if (data.agency_id) LocalStorage.updateAgencyId(String(data.agency_id));
            LocalStorage.updateAgencyReportModality(fresh, data.agency_id);
            setModality(fresh);
            setLoaded(true);
        }).catch(() => {
            setLoaded(true);
            // Sin red: nos quedamos con el seed. La modalidad ausente ⇒ V1 (deploy skew safe).
        });
        return () => { cancelled = true; };
    }, []);

    return {
        modality,
        isClientReports: modality === REPORT_MODALITY_CLIENT_REPORTS,
        // `known`: hay con qué decidir sin parpadear — un seed persistido o la respuesta ya llegó.
        // El portal lo usa para no montar V1 (con su request) mientras espera la modalidad.
        known: seeded || loaded,
        loaded,
    };
}

/** Lectura sincrónica (class components y helpers): lo último que se persistió. */
export function readReportModality() {
    return LocalStorage.fetchAgencyReportModality();
}

export function isClientReportsModality(modality) {
    return modality === REPORT_MODALITY_CLIENT_REPORTS;
}

/**
 * A dónde lleva «explorar los datos de este cliente» en esta agencia: `/informes` en la
 * modalidad nueva, `/data-explorer` en la vieja. Lectura sincrónica de lo último persistido,
 * la misma que usan el sidebar (`Layout`) y la pestaña del cliente (`ClientTabs`).
 *
 * Existe porque los botones sueltos («Explorar datos» del panel, la acción rápida del control
 * central, los CTA de la sección de reportes) tenían la ruta vieja escrita a mano y en la
 * modalidad nueva mandaban al Explorador (QA UI-20).
 */
export function explorerPath() {
    return explorerIsInformes() ? '/informes' : '/data-explorer';
}

/**
 * Si esos mismos accesos llevan a Informes. El RÓTULO sigue al destino (card zoQAjjra): un botón
 * que dice «Explorar datos» y abre Informes se confunde con el Explorador, y la función se llama
 * «Informes» en todo lo que el usuario lee. En la modalidad vieja el destino sí es el Explorador y
 * el rótulo viejo es el correcto.
 */
export function explorerIsInformes() {
    return isClientReportsModality(readReportModality());
}
